import React, { useMemo } from 'react'
import {
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    Label,
    Transition,
    type ListboxButtonProps,
} from '@headlessui/react'
import { lit, buildClassName } from '../../../utils'
import {
    CheckIcon,
    ChevronUpDownIcon,
    ExclamationCircleIcon,
} from '../../icons'

export interface SelectOption {
    value: string
    name: string
}

type RenderListboxButtonArgs = {
    selectedOption?: SelectOption
    hasError?: boolean
    ListboxButton: typeof ListboxButton
}

export interface SelectProps {
    /**
     * The classname -- this is mostly used to hook GA event tracking
     */
    className?: string

    /** The class name to be used for GTM event tracking
     */
    analyticsClassName?: string

    /**
     * The value displayed by the dropdown.
     */
    value?: string

    /**
     * Optional default value, only relevant if you want an uncontrolled component
     */
    defaultValue?: string

    /**
     * The label elements is useful for assistive technologies such as screen readers.
     */
    label: string

    /**
     * The options is an array of objects with label and value keys used to populate the dropdown.
     */
    options: SelectOption[]

    hasError?: boolean

    labelPlacement?: 'inline' | 'above'

    id?: string

    name: string

    /**
     * Prop to customize the width of the listbox options menu.
     * Default is the full width of the listbox button
     */
    listboxOptionsClassName?: string

    /**
     * Render prop to customize the appearance of the Listbox button that opens the menu.
     * Your render function should always return a ListboxButton element.
     */
    renderListboxButton?: (
        args: RenderListboxButtonArgs
    ) => React.ReactElement<ListboxButtonProps>

    onChange?: (option: SelectOption) => void

    onBlur?: (details: { selectedOption: SelectOption }) => void
}

/**
 * Note about the ref:
 * This component allows you to attach a ref and read its value like this:

    const myRef = React.useRef<HTMLInputElement>()
    const currentValue = myRef?.current.value
    ...
    <Select ref={myRef}

 * The ref is attached to the hidden input used to track the current value.
 * If using react-hook-form, you will need to write your own onChange handler in order for
 * the form state to stay in sync and validation to work correctly, because react-hook-form's
 * onChange and onBlur handlers only work in response to DOM events on native form elements.
 */
export const Select = React.forwardRef<HTMLInputElement, SelectProps>(
    (
        {
            options,
            value,
            defaultValue,
            id,
            name,
            className = '',
            analyticsClassName = buildClassName('Select'),
            label = 'Select an option',
            hasError,
            labelPlacement = 'inline',
            onChange,
            onBlur,
            renderListboxButton,
            listboxOptionsClassName = 'w-full',
        }: SelectProps,
        ref
    ) => {
        // should we make this component behave like a controlled or uncontrolled component?
        const isControlled = value !== undefined

        const defaultValueOption = useMemo(() => {
            const found = options.find(
                (option) => option.value === defaultValue
            )
            if (found) {
                return found
            }
            return labelPlacement === 'inline' ? undefined : options[0]
        }, [options, defaultValue, labelPlacement])

        // The currently selected option. This is only used when isControlled is true
        const controlledModeSelectedOption = useMemo(
            () => options.find((option) => option.value === value),
            [options, value]
        )

        function getCurrentValue() {
            return controlledModeSelectedOption ?? { name: label, value: '' }
        }

        function defaultRenderListboxButton({
            selectedOption,
        }: RenderListboxButtonArgs) {
            return (
                <ListboxButton
                    className={lit`relative w-full cursor-default border border-neutral-300 bg-white px-3 py-2 text-left ${
                        !selectedOption?.value && 'text-neutral-500'
                    } focus-visible:ring-2 ${
                        hasError && 'border-state-error border'
                    }`}
                >
                    <span className="line-clamp-1 block w-11/12 whitespace-nowrap text-base leading-8">
                        {selectedOption ? selectedOption.name : label}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        {hasError ? (
                            <ExclamationCircleIcon
                                className="text-state-error h-5 w-5"
                                aria-hidden="true"
                            />
                        ) : (
                            <ChevronUpDownIcon
                                className="h-5 w-5 text-neutral-500"
                                aria-hidden="true"
                            />
                        )}
                    </span>
                </ListboxButton>
            )
        }

        if (!renderListboxButton) {
            renderListboxButton = defaultRenderListboxButton
        }

        return (
            <Listbox
                value={isControlled ? getCurrentValue() : undefined}
                defaultValue={defaultValueOption}
                onChange={(selectedOption) => {
                    onChange && onChange(selectedOption)
                }}
            >
                {({ open, value: selectedOption }) => (
                    <div>
                        <input
                            type="hidden"
                            ref={ref}
                            id={id ?? name}
                            name={name}
                            value={selectedOption?.value}
                        />
                        {label && labelPlacement !== 'inline' && (
                            <Label
                                htmlFor={id ?? name}
                                className="mb-1 block text-left text-sm leading-6 text-neutral-900"
                            >
                                {label}
                            </Label>
                        )}
                        <div
                            className={lit`relative ${className} ${analyticsClassName}`}
                            onBlur={() => onBlur && onBlur({ selectedOption })}
                        >
                            {renderListboxButton({
                                selectedOption,
                                hasError,
                                ListboxButton,
                            })}
                            <Transition
                                show={open}
                                as={React.Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <ListboxOptions
                                    modal={false}
                                    className={lit`absolute z-50 mt-1 max-h-96 overflow-auto border-[1px] border-neutral-300 bg-white py-1 text-left text-base shadow-lg focus:outline-none ${listboxOptionsClassName}`}
                                >
                                    {options.map((option) => (
                                        <ListboxOption
                                            key={option.value}
                                            className={({ selected }) =>
                                                lit`
                                                ${
                                                    selected
                                                        ? 'bg-indigo-600 text-neutral-400'
                                                        : 'text-black'
                                                } relative cursor-default select-none py-2 pl-3 pr-4 data-[focus]:bg-neutral-100`
                                            }
                                            value={option}
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span
                                                        className={lit`
                                                        ${
                                                            selected
                                                                ? 'font-semibold text-black'
                                                                : 'font-normal'
                                                        } block truncate`}
                                                    >
                                                        {option.name}
                                                    </span>

                                                    {selected ? (
                                                        <span
                                                            className={lit`
                                                            ${
                                                                selected
                                                                    ? 'text-neutral-400'
                                                                    : 'text-indigo-900'
                                                            } absolute inset-y-0 right-0 flex items-center pr-2`}
                                                        >
                                                            <CheckIcon
                                                                className="h-5 w-5"
                                                                aria-hidden="true"
                                                            />
                                                        </span>
                                                    ) : null}
                                                </>
                                            )}
                                        </ListboxOption>
                                    ))}
                                </ListboxOptions>
                            </Transition>
                        </div>
                    </div>
                )}
            </Listbox>
        )
    }
)

Select.displayName = 'Select'
